import { GetHomeConfigDto } from "../../services/HomeConfig/dto/get.dto";

export type ConfigurationType = "base" | "region" | "community" | "user";

export type NodeBucket = "nodes" | "uncheckedNodes";

export type ConfigNodeType =
  | "material"
  | "visibility"
  | "mesh"
  | "transform"
  | "map"
  | "teleport"
  | "trigger";

export type DependencyGroupValue = {
  value: string;
  isSelect: boolean;
};

export type RefEntry<V> = [string, V];

export interface OptionNameParts {
  group: string;
  leaf: string;
}

// NOTE: Every ref-holding field any node type carries, so a node of any type can
// be read through one shape — `type` keeps it from being an all-optional weak type.
export interface NodeRefFields {
  type: ConfigNodeType;
  tagsOption?: Record<string, string>;
  filterByOption?: Record<string, string>;
  options?: Record<string, string>;
  dependencyGroup?: Record<string, DependencyGroupValue>;
}

// One rewrite per ref shape: a value pointing at an option or group, a bare
// group, and a group + leaf pair keyed by its group.
export interface RefConverter {
  ref: (value: string) => string;
  group: (value: string) => string;
  pair: (group: string, leaf: string) => RefEntry<string>;
}

// Fields shared by every config node, regardless of type.
export interface BaseConfigNode {
  filterBy: Record<string, string>;
  tags?: Record<string, string>;
  dependencyGroup?: Record<string, DependencyGroupValue>;
}

export interface MaterialConfigNode extends BaseConfigNode {
  type: "material";
  materialName: string;
  materialId?: string;
}

export interface VisibilityConfigNode extends BaseConfigNode {
  type: "visibility";
  meshName?: string;
  filterByOption?: Record<string, string>;
  tagsOption?: Record<string, string>;
}

export interface MeshConfigNode extends BaseConfigNode {
  type: "mesh";
  meshName: string;
}

export interface Vector3 {
  x: number;
  y: number;
  z: number;
}

export interface TransformConfigNode extends BaseConfigNode {
  type: "transform";
  locationOffset: Vector3;
}

export interface TeleportInfo {
  location: Vector3;
  rotation: Vector3;
}

export interface TeleportConfigNode {
  type: "teleport";
  location: Vector3;
  rotation: Vector3;
}

export interface TriggerConfigNode {
  type: "trigger";
  options: Record<string, string>;
}

export interface FloorMap {
  // NOTE: The floor's identity — areas reference it and the buyer lines floors
  // up across options by it. Authored, so a basement can be -1.
  floorIndex: number;
  // NOTE: Authored on the base config only; option nodes leave it blank and the
  // buyer resolves the label by floorIndex. Blank everywhere falls back to the
  // index itself.
  floorName?: string;
  imageUrl: string;
}

// NOTE: x/y/width/height are percentages of the floor image box so one authored
// area lands identically on any screen size; the buyer draws it with CSS
// percentages and never measures pixels.
export interface AreaHighlight {
  floorIndex: number;
  name?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  teleport?: TeleportInfo;
}

export interface MapConfigNode extends BaseConfigNode {
  type: "map";
  floorMaps: FloorMap[];
  areaHighlights: AreaHighlight[];
}

export type ConfigNode =
  | MaterialConfigNode
  | VisibilityConfigNode
  | MeshConfigNode
  | TransformConfigNode
  | MapConfigNode
  | TeleportConfigNode
  | TriggerConfigNode;

export type OptionLayout =
  | "default"
  | "tile-carousel"
  | "list-row"
  | "photo-card"
  | "photo-hero"
  | "scheme-card";

export interface OptionPresentation {
  layout?: OptionLayout;
  thumbnail?: string;
  tags?: Record<string, string | number>;
  displayKey?: string;
  uncheckedValue?: string;
}

export type ConfigLayout = {
  value: OptionLayout;
  label: string;
};

export enum UnitOfMeasurement {
  MetricUnit = "MetricUnit",
  ImperialUnit = "ImperialUnit",
}

export interface ConfigOptionDependency {
  triggers?: string[];
}

export interface ConfigOption {
  id?: string;
  name: string;
  // Note: these values will be used later.
  order: number;
  customGroup: string;
  price: number;
  dependency: ConfigOptionDependency;
  // end Note

  nodes: ConfigNode[];
  // NOTE: only the "list-row" layout authors this — homeBuyer executes it when
  // the buyer unchecks the item. Same message format as `nodes`, so Unreal is
  // untouched. Absent when empty, so the config JSON stays sparse.
  uncheckedNodes?: ConfigNode[];
  useHighlightEffect: boolean;
  show2DMap: boolean;
  highlightGroup: number;
  value?: string;
  unitOfMeasurement?: UnitOfMeasurement;
  presentation?: OptionPresentation;
}

export interface HomeConfigFormData {
  id: string;
  homeId: string;
  homeName: string;
  type: ConfigurationType;
  scopeName?: string;
  region?: string;
  community?: string;
  userId?: string;
  options: ConfigOption[];
}

// Nested tree node built by HomeConfigUsecase.getConfigTree; antd's
// native tree-data renders `children` at every depth
export interface ConfigTreeNode {
  key: string;
  config: GetHomeConfigDto;
  type: ConfigurationType;
  displayName?: string;
  // Nesting level (base = 0); used to indent the Name cell so the hierarchy
  // reads clearly even though the expander lives in its own column.
  depth: number;
  // Omitted for leaf nodes: antd draws a tree expander whenever this field is
  // present (even an empty array), so leaves must have no `children` key.
  children?: ConfigTreeNode[];
}
